import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { restartableTask, waitForQueue } from 'ember-concurrency';
import moment from 'moment-timezone';
import { monthRange, monthGrid, addDays, startOfWeek, addMonths } from 'beebee-base/lib/period';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("<div\n  class=\'bb-pp-cal\'\n  data-months={{this.monthsCount}}\n  data-compact={{if @compact \'true\'}}\n  {{did-insert this.registerElement}}\n  {{on \'mouseleave\' (fn this.hover null)}}\n  ...attributes\n>\n  {{!-- key=\'@index\': an identity key (the YYYY-MM id) changes on every ‹ ›, so the month head holding the pressed\n       nav button would be replaced, focus would fall to <body> and Escape would no longer reach the panel root --}}\n  {{#each this.monthBlocks key=\'@index\' as |mb|}}\n    <div class=\'bb-pp-month\' data-test-rc-month={{mb.id}}>\n      <div class=\'bb-pp-month-head\'>\n        {{#if mb.isFirst}}\n          <button\n            type=\'button\'\n            class=\'bb-pp-nav bb-pp-nav--prev\'\n            aria-label={{this.prevLabel}}\n            data-test-rc-prev\n            {{on \'click\' this.prevMonth}}\n          >\n            <svg\n              width=\'14\'\n              height=\'14\'\n              viewBox=\'0 0 24 24\'\n              fill=\'none\'\n              stroke=\'currentColor\'\n              stroke-width=\'2.2\'\n              stroke-linecap=\'round\'\n              stroke-linejoin=\'round\'\n              aria-hidden=\'true\'\n              focusable=\'false\'\n            >\n              <path d=\'M15 18l-6-6 6-6\'></path>\n            </svg>\n          </button>\n        {{/if}}\n        <button\n          type=\'button\'\n          class=\'bb-pp-month-title\'\n          aria-label={{mb.ariaLabel}}\n          {{on \'click\' (fn this.pickRange mb.from mb.to)}}\n        >\n          {{mb.title}}\n        </button>\n        {{#if mb.isLast}}\n          <button\n            type=\'button\'\n            class=\'bb-pp-nav bb-pp-nav--next\'\n            aria-label={{this.nextLabel}}\n            data-test-rc-next\n            {{on \'click\' this.nextMonth}}\n          >\n            <svg\n              width=\'14\'\n              height=\'14\'\n              viewBox=\'0 0 24 24\'\n              fill=\'none\'\n              stroke=\'currentColor\'\n              stroke-width=\'2.2\'\n              stroke-linecap=\'round\'\n              stroke-linejoin=\'round\'\n              aria-hidden=\'true\'\n              focusable=\'false\'\n            >\n              <path d=\'M9 18l6-6-6-6\'></path>\n            </svg>\n          </button>\n        {{/if}}\n      </div>\n      <table role=\'grid\' aria-label={{mb.title}}>\n        <thead role=\'rowgroup\'>\n          <tr role=\'row\'>\n            {{#if this.showWeekNumbers}}\n              <th scope=\'col\' aria-hidden=\'true\'></th>\n            {{/if}}\n            {{#each this.weekdays as |wd|}}\n              <th scope=\'col\' abbr={{wd.long}}>{{wd.short}}</th>\n            {{/each}}\n          </tr>\n        </thead>\n        <tbody role=\'rowgroup\'>\n          {{!-- key=\'@index\': monthBlocks is rebuilt when the tab stop moves (focus on mousedown); identity keys\n               would replace the row and the click would land on a detached button --}}\n          {{#each mb.weeks key=\'@index\' as |w|}}\n            <tr role=\'row\'>\n              {{#if this.showWeekNumbers}}\n                <td>\n                  <button\n                    type=\'button\'\n                    class=\'bb-pp-wk\'\n                    aria-label={{w.ariaLabel}}\n                    data-test-rc-week={{w.weekNo}}\n                    {{on \'click\' (fn this.pickRange w.from w.to)}}\n                  >\n                    {{w.weekNo}}\n                  </button>\n                </td>\n              {{/if}}\n              {{#each w.days key=\'@index\' as |d|}}\n                {{#if d.key}}\n                  <td role=\'gridcell\' class={{d.cellClass}} aria-selected={{d.selected}}>\n                    <button\n                      type=\'button\'\n                      class={{d.dayClass}}\n                      tabindex={{if d.isTab \'0\' \'-1\'}}\n                      aria-label={{d.ariaLabel}}\n                      aria-pressed={{d.pressed}}\n                      aria-current={{if d.isToday \'date\'}}\n                      data-test-rc-day={{d.key}}\n                      {{on \'click\' (fn this.pickDay d.key)}}\n                      {{on \'mouseenter\' (fn this.hover d.key)}}\n                      {{on \'focus\' (fn this.focusDay d.key)}}\n                      {{on \'keydown\' (fn this.onKeyDown d.key)}}\n                    >\n                      {{d.day}}\n                    </button>\n                  </td>\n                {{else}}\n                  <td role=\'gridcell\' aria-hidden=\'true\'></td>\n                {{/if}}\n              {{/each}}\n            </tr>\n          {{/each}}\n        </tbody>\n      </table>\n    </div>\n  {{/each}}\n</div>");

var _class, _descriptor, _descriptor2;
function pad(n) {
  return (n < 10 ? '0' : '') + n;
}
function monthStartKey(y, m) {
  return `${y}-${pad(m + 1)}-01`;
}

// Normalises a (year, month) pair whose month may be out of 0..11.
function ym(y, m) {
  const total = y * 12 + m;
  return {
    y: Math.floor(total / 12),
    m: (total % 12 + 12) % 12
  };
}
function keyYM(key) {
  return {
    y: Number(key.slice(0, 4)),
    m: Number(key.slice(5, 7)) - 1
  };
}
function capitalise(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

// Band classes for the <td>: a start==end single day gets no band.
function bandClass(key, lo, hi, inRange) {
  if (!inRange || lo === hi) return '';
  const out = ['bb-pp-cell--band'];
  if (key === lo) out.push('bb-pp-cell--band-start');
  if (key === hi) out.push('bb-pp-cell--band-end');
  return out.join(' ');
}
function dayClass(key, {
  today,
  isEnd,
  pending
}) {
  const out = ['bb-pp-day'];
  if (key === today) out.push('bb-pp-day--today');
  if (isEnd) out.push('bb-pp-day--end');
  if (pending && key === pending) out.push('bb-pp-day--pending');
  return out.join(' ');
}
let BbRangeCalendar = (_class = class BbRangeCalendar extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "intl", _descriptor, this);
    // Day holding the roving tabindex (0); null = derive from the args.
    _initializerDefineProperty(this, "focusKey", _descriptor2, this);
    this.element = null;
  }
  get monthsCount() {
    return Number(this.args.months) === 2 ? 2 : 1;
  }
  get fdow() {
    const f = this.args.firstDayOfWeek;
    return f === undefined || f === null ? moment.localeData().firstDayOfWeek() : Number(f);
  }
  get showWeekNumbers() {
    return this.args.weekNumbers !== false;
  }
  get todayKey() {
    return this.args.today || moment().format('YYYY-MM-DD');
  }

  // First visible month, normalised (a month outside 0..11 rolls the year).
  get view() {
    const {
      year,
      month
    } = this.args;
    const y = year === null || year === undefined || year === '' ? NaN : Number(year);
    const m = month === null || month === undefined || month === '' ? NaN : Number(month);
    const t = keyYM(this.todayKey);
    return ym(Number.isFinite(y) ? y : t.y, Number.isFinite(m) ? m : t.m);
  }
  get viewYear() {
    return this.view.y;
  }
  get viewMonth() {
    return this.view.m;
  }

  // First and last visible day keys.
  get visibleStart() {
    return monthStartKey(this.viewYear, this.viewMonth);
  }
  get visibleEnd() {
    const last = ym(this.viewYear, this.viewMonth + this.monthsCount - 1);
    return monthRange(last.y, last.m)[1];
  }

  // [lo, hi] actually painted: preview wins over the committed draft.
  get span() {
    const {
      previewFrom,
      previewTo,
      from,
      to
    } = this.args;
    let lo, hi;
    if (previewFrom || previewTo) {
      lo = previewFrom || null;
      hi = previewTo || null;
    } else {
      lo = from || null;
      hi = to || null;
    }
    if (lo && hi && hi < lo) [lo, hi] = [hi, lo];
    return {
      lo,
      hi
    };
  }
  get weekdays() {
    const short = moment.weekdaysMin();
    const long = moment.weekdays();
    const out = [];
    for (let i = 0; i < 7; i++) {
      const d = (this.fdow + i) % 7;
      out.push({
        short: short[d],
        long: long[d]
      });
    }
    return out;
  }

  // Which visible day carries tabindex=0.
  get tabKey() {
    const inView = k => k && k >= this.visibleStart && k <= this.visibleEnd;
    if (inView(this.focusKey)) return this.focusKey;
    const {
      lo,
      hi
    } = this.span;
    if (inView(this.args.pendingKey)) return this.args.pendingKey;
    if (inView(lo)) return lo;
    if (inView(hi)) return hi;
    if (inView(this.todayKey)) return this.todayKey;
    return this.visibleStart;
  }
  get monthBlocks() {
    const {
      lo,
      hi
    } = this.span;
    const pending = this.args.pendingKey || null;
    const today = this.todayKey;
    const tabKey = this.tabKey;
    const blocks = [];
    for (let i = 0; i < this.monthsCount; i++) {
      const {
        y,
        m
      } = ym(this.viewYear, this.viewMonth + i);
      const title = capitalise(moment(monthStartKey(y, m), 'YYYY-MM-DD').format('MMMM YYYY'));
      const [mFrom, mTo] = monthRange(y, m);
      const grid = monthGrid(y, m, this.fdow);
      const weeks = (grid?.weeks || []).map(w => ({
        weekNo: w.weekNo,
        from: w.from,
        to: w.to,
        ariaLabel: this.intl.t('period_picker.aria.week', {
          week: w.weekNo
        }),
        days: (w.days || []).map(d => this._dayCell(d, {
          lo,
          hi,
          pending,
          today,
          tabKey
        }))
      }));
      blocks.push({
        y,
        m,
        id: `${y}-${pad(m + 1)}`,
        title,
        from: mFrom,
        to: mTo,
        ariaLabel: this.intl.t('period_picker.aria.month', {
          month: title
        }),
        isFirst: i === 0,
        isLast: i === this.monthsCount - 1,
        weeks
      });
    }
    return blocks;
  }
  _dayCell(d, {
    lo,
    hi,
    pending,
    today,
    tabKey
  }) {
    const key = d?.key && d.inMonth !== false ? d.key : null;
    if (!key) return {
      key: null,
      day: d?.day ?? null
    };
    const isEnd = key === lo || key === hi;
    const inRange = !!(lo && hi && key >= lo && key <= hi);
    return {
      key,
      day: d.day,
      cellClass: bandClass(key, lo, hi, inRange),
      dayClass: dayClass(key, {
        today,
        isEnd,
        pending
      }),
      selected: inRange || isEnd ? 'true' : 'false',
      pressed: isEnd ? 'true' : 'false',
      isToday: key === today,
      ariaLabel: moment(key, 'YYYY-MM-DD').format('D MMMM YYYY'),
      isTab: key === tabKey
    };
  }
  get prevLabel() {
    return this.intl.t('period_picker.aria.prev_month');
  }
  get nextLabel() {
    return this.intl.t('period_picker.aria.next_month');
  }
  registerElement(el) {
    this.element = el;
  }
  prevMonth() {
    this.stepMonth(-1);
  }
  nextMonth() {
    this.stepMonth(1);
  }

  // ‹ ›: the month heads are keyed by position, so the nav button survives the re-render; if the
  // host re-renders it anyway, focus is put back on it (keyboard users keep their place).
  stepMonth(dir) {
    const {
      y,
      m
    } = ym(this.viewYear, this.viewMonth + dir);
    const selector = dir < 0 ? '[data-test-rc-prev]' : '[data-test-rc-next]';
    const button = this.element?.querySelector(selector);
    const hadFocus = Boolean(button && typeof document !== 'undefined' && document.activeElement === button);
    this.args.onView?.(y, m);
    if (hadFocus) this.navFocusTask.perform(selector);
  }
  pickDay(key) {
    this.focusKey = key;
    this.args.onPickDay?.(key);
  }
  pickRange(from, to) {
    this.args.onPickRange?.(from, to);
  }
  hover(key) {
    this.args.onHover?.(key);
  }
  focusDay(key) {
    if (this.focusKey !== key) this.focusKey = key;
    this.args.onHover?.(key);
  }

  // Bound via (fn this.onKeyDown day.key): the DOM event arrives deliberately as `event`.
  onKeyDown(key, event) {
    if (!event || !key) return;
    let next = null;
    switch (event.key) {
      case 'ArrowLeft':
        next = addDays(key, -1);
        break;
      case 'ArrowRight':
        next = addDays(key, 1);
        break;
      case 'ArrowUp':
        next = addDays(key, -7);
        break;
      case 'ArrowDown':
        next = addDays(key, 7);
        break;
      case 'PageUp':
        next = addMonths(key, event.shiftKey ? -12 : -1);
        break;
      case 'PageDown':
        next = addMonths(key, event.shiftKey ? 12 : 1);
        break;
      case 'Home':
        next = startOfWeek(key, this.fdow);
        break;
      case 'End':
        next = addDays(startOfWeek(key, this.fdow), 6);
        break;
      case 'Enter':
      case ' ':
      case 'Spacebar':
        // Handled here (not by the native click) so the pick is not doubled.
        event.preventDefault();
        this.pickDay(key);
        return;
      default:
        return;
    }
    event.preventDefault();
    if (!next) return;
    this.moveFocus(next);
  }
  moveFocus(next) {
    this.focusKey = next;
    if (next < this.visibleStart) {
      const {
        y,
        m
      } = keyYM(next);
      this.args.onView?.(y, m);
    } else if (next > this.visibleEnd) {
      const t = keyYM(next);
      const {
        y,
        m
      } = ym(t.y, t.m - (this.monthsCount - 1));
      this.args.onView?.(y, m);
    }
    this.focusTask.perform(next);
  }
  *navFocusTask(selector) {
    yield waitForQueue('afterRender');
    const root = this.element;
    if (this.isDestroying || this.isDestroyed || !root?.isConnected) return;
    const button = root.querySelector(selector);
    if (button && document.activeElement !== button) button.focus();
  }

  // Waits for the host to re-render the new view, then moves DOM focus. A task (not a
  // runloop schedule) so a teardown cancels it at the yield.
  *focusTask(key) {
    yield waitForQueue('afterRender');
    const root = this.element;
    if (this.isDestroying || this.isDestroyed || !root) return;
    root.querySelector(`[data-test-rc-day="${key}"]`)?.focus();
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "intl", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "focusKey", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "registerElement", [action], Object.getOwnPropertyDescriptor(_class.prototype, "registerElement"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "prevMonth", [action], Object.getOwnPropertyDescriptor(_class.prototype, "prevMonth"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "nextMonth", [action], Object.getOwnPropertyDescriptor(_class.prototype, "nextMonth"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "pickDay", [action], Object.getOwnPropertyDescriptor(_class.prototype, "pickDay"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "pickRange", [action], Object.getOwnPropertyDescriptor(_class.prototype, "pickRange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "hover", [action], Object.getOwnPropertyDescriptor(_class.prototype, "hover"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "focusDay", [action], Object.getOwnPropertyDescriptor(_class.prototype, "focusDay"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onKeyDown", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onKeyDown"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "navFocusTask", [restartableTask], Object.getOwnPropertyDescriptor(_class.prototype, "navFocusTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "focusTask", [restartableTask], Object.getOwnPropertyDescriptor(_class.prototype, "focusTask"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, BbRangeCalendar);

export { BbRangeCalendar as default };
//# sourceMappingURL=bb-range-calendar.js.map
